import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GraduationCap, Briefcase, Award, HandHeart, ScanLine, Sparkles, BookOpen,
  Star, ArrowUpRight, CheckCircle2, FileBadge, Building2, Layers, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TierBadge from "@/components/TierBadge";

export default function Students() {
  return (
    <div className="bg-ec-bg text-ec-ink min-h-screen" data-testid="students-page">
      <Navbar />
      <Hero />
      <CareerPathStrip />
      <WhyStudents />
      <WhatGetsVerified />
      <StudentScore />
      <Steps />
      <CampusPlacement />
      <StudentPricing />
      <FinalCta />
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-12 lg:pt-28 lg:pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div
          className="lg:col-span-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 border border-ec-line bg-white px-3 py-1 text-xs eyebrow">
            <GraduationCap className="w-3.5 h-3.5" /> For Students
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6">
            Start your career <br />
            with a <span className="text-ec-emerald">verified</span> reputation.
          </h1>
          <p className="text-lg sm:text-xl text-ec-ink-soft mt-7 max-w-xl leading-relaxed">
            Your first internship. Your first project. Your first recommendation. EthicCred turns
            every milestone of your student years into a tamper-proof Professional Passport that
            recruiters can verify in five seconds.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="students-hero-register">
              <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 px-6 h-12 text-base gap-2">
                Claim my Student Passport <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/verify" data-testid="students-hero-verify">
              <Button variant="outline" className="rounded-sm h-12 border-ec-ink px-6 text-base gap-2 hover:bg-ec-mute">
                <ScanLine className="w-4 h-4" /> See a verified passport
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl border-t border-ec-line pt-6">
            <Stat n="Free" l="Forever" />
            <Stat n="900+" l="Institutions" />
            <Stat n="60s" l="To claim" />
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-6"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <StudentHeroVisual />
        </motion.div>
      </div>
    </section>
  );
}

function StudentHeroVisual() {
  return (
    <div className="relative w-full max-w-[560px] mx-auto" data-testid="student-hero-visual">
      {/* Backdrop card */}
      <div className="absolute inset-0 bg-ec-ink rounded-sm rotate-2 translate-x-3 translate-y-3 -z-10" />

      <div className="relative bg-ec-soft border border-ec-ink/15 p-8 noise-overlay overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.18)]">
        <div className="absolute inset-0 pointer-events-none opacity-[0.04] text-[120px] font-display font-black flex items-center justify-center select-none">
          ETHICCRED
        </div>

        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-2 text-ec-ink">
            <div className="w-7 h-7 bg-ec-ink text-white flex items-center justify-center">
              <ShieldCheck className="w-3.5 h-3.5" />
            </div>
            <span className="eyebrow text-ec-ink">Student Passport</span>
          </div>
          <TierBadge tier="Gold" size="sm" />
        </div>

        <div className="relative mt-5">
          <h3 className="font-display text-3xl font-black tracking-tight">Riya Sharma</h3>
          <p className="text-ec-ink-soft text-sm mt-1">B.Tech CS · NIT Trichy · Class of 2027</p>
        </div>

        <div className="relative mt-6 flex items-end justify-between">
          <div>
            <div className="eyebrow">EthicCred Score</div>
            <div className="font-display text-6xl font-black tracking-tighter">540</div>
            <div className="font-mono-plex text-xs text-ec-ink-soft mt-1">/ 1000 · Top 12%</div>
          </div>
          <div className="text-right">
            <div className="eyebrow">EthicCred ID</div>
            <div className="font-mono-plex font-bold text-base mt-1 tracking-wider">EC-7G2H-S4MR</div>
          </div>
        </div>

        {/* Score factor mini bars */}
        <div className="relative mt-6 space-y-2">
          {[
            ["Education", 75, 150],
            ["Conduct", 180, 250],
            ["Internships", 50, 50],
            ["Recommendations", 60, 100],
            ["Skills", 70, 100],
          ].map(([label, v, max]) => (
            <div key={label}>
              <div className="flex justify-between text-[11px]">
                <span className="font-semibold">{label}</span>
                <span className="font-mono-plex"><span className="text-ec-emerald">+{v}</span><span className="text-ec-ink-soft">/{max}</span></span>
              </div>
              <div className="h-1 bg-ec-mute mt-0.5 overflow-hidden">
                <motion.div
                  className="h-full bg-ec-ink"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${(v / max) * 100}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="relative mt-6 border-t border-dashed border-ec-ink/15 pt-5 grid grid-cols-3 gap-3 text-center">
          <MiniStat label="Internships" v="2" />
          <MiniStat label="Recommendations" v="3" />
          <MiniStat label="Projects" v="5" />
        </div>

        <div className="absolute right-2 bottom-2 stamp text-ec-emerald">Verified</div>
      </div>

      {/* Floating badges — all positioned to the right of the card to avoid the headline */}
      <motion.div
        className="hidden xl:block absolute left-full top-12 ml-2 ec-float"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.4 }}
      >
        <FloatChip icon={Briefcase} label="Internship signed" />
      </motion.div>
      <motion.div
        className="hidden xl:block absolute left-full top-44 ml-2 ec-float ec-float-delay-1"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.6 }}
      >
        <FloatChip icon={Star} label="3 recommendations" emerald />
      </motion.div>
      <motion.div
        className="hidden xl:block absolute left-full bottom-16 ml-2 ec-float ec-float-delay-2"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.8 }}
      >
        <FloatChip icon={Award} label="Gold tier reached" emerald />
      </motion.div>
    </div>
  );
}

function MiniStat({ label, v }) {
  return (
    <div>
      <div className="font-display text-2xl font-black tracking-tighter">{v}</div>
      <div className="eyebrow mt-0.5">{label}</div>
    </div>
  );
}

function FloatChip({ icon: Icon, label, emerald }) {
  return (
    <div className={`bg-white border ${emerald ? "border-ec-emerald" : "border-ec-line"} shadow-[0_8px_24px_rgba(0,0,0,0.06)] px-3 py-2 flex items-center gap-2 rounded-sm`}>
      <Icon className={`w-3.5 h-3.5 ${emerald ? "text-ec-emerald" : "text-ec-ink"}`} />
      <span className={`text-[11px] font-semibold ${emerald ? "text-ec-emerald" : "text-ec-ink"}`}>{label}</span>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-3xl font-black tracking-tight">{n}</div>
      <div className="eyebrow mt-1">{l}</div>
    </div>
  );
}

function CareerPathStrip() {
  const stages = [
    { i: BookOpen, t: "Study" },
    { i: GraduationCap, t: "Internship" },
    { i: HandHeart, t: "Volunteer" },
    { i: FileBadge, t: "Projects" },
    { i: Star, t: "Recommendations" },
    { i: Briefcase, t: "First Job" },
  ];
  return (
    <div className="border-y border-ec-line bg-ec-soft">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-8 flex flex-wrap items-center justify-between gap-4">
        <span className="eyebrow">Every stage builds your passport</span>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {stages.map((s, i) => (
            <div key={s.t} className="flex items-center gap-2">
              <s.i className="w-4 h-4 text-ec-emerald" />
              <span className="font-display font-bold text-sm">{s.t}</span>
              {i < stages.length - 1 && <span className="font-mono text-xs text-ec-ink-soft ml-4">→</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WhyStudents() {
  const items = [
    { icon: Award, t: "Your CV writes itself", d: "Every certificate, project and recommendation lives on your passport. No more chasing paperwork at graduation." },
    { icon: ScanLine, t: "Recruiters trust the QR", d: "Instead of explaining where you interned, just share the link. Employers see a verified record signed by the source." },
    { icon: Sparkles, t: "Compete with experience, not titles", d: "First-year student with two verified internships beats a third-year with a blank CV. EthicCred Score is earned, not edited." },
    { icon: Layers, t: "Lifetime portable", d: "Your passport follows you across colleges, cities and countries. Built once, used forever." },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24" data-testid="why-students">
      <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
        <div className="lg:col-span-6">
          <div className="eyebrow">Why students choose EthicCred</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            College ends. Your record lasts.
          </h2>
        </div>
        <p className="lg:col-span-5 lg:col-start-8 text-ec-ink-soft leading-relaxed">
          You'll spend the next decade explaining what you did in college. EthicCred makes
          that explanation a one-click verification.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-px bg-ec-line border border-ec-line">
        {items.map((it, i) => (
          <motion.div
            key={it.t}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="bg-white p-8"
          >
            <it.icon className="w-6 h-6 text-ec-emerald" />
            <h3 className="font-display text-xl font-bold mt-4">{it.t}</h3>
            <p className="text-sm text-ec-ink-soft mt-2 leading-relaxed">{it.d}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function WhatGetsVerified() {
  const items = [
    { icon: GraduationCap, t: "Degree & marks", d: "Issued straight from your institution.", pts: "+150" },
    { icon: Briefcase, t: "Internships", d: "Signed by your internship coordinator.", pts: "+50" },
    { icon: HandHeart, t: "Volunteer work", d: "NGOs, college clubs, community drives.", pts: "+50" },
    { icon: FileBadge, t: "Projects", d: "Capstones, hackathons, research output.", pts: "+50" },
    { icon: Star, t: "Recommendations", d: "From professors, managers, mentors.", pts: "+100" },
    { icon: Award, t: "Conduct certificate", d: "Workplace and campus conduct ratings.", pts: "+250" },
  ];
  return (
    <section className="bg-ec-ink text-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <div className="eyebrow text-zinc-400">What gets verified</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
              Every milestone. Verified at source.
            </h2>
          </div>
          <p className="lg:col-span-5 lg:col-start-8 text-zinc-400 leading-relaxed">
            Records can only be signed by the organisation that issued them. That's why an EthicCred
            verification means something — and a PDF doesn't.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800" data-testid="student-verified-items">
          {items.map((it) => (
            <div key={it.t} className="bg-ec-ink p-7 hover:bg-zinc-900 transition-colors">
              <div className="flex justify-between items-start">
                <it.icon className="w-6 h-6 text-ec-emerald" />
                <span className="font-mono text-xs text-ec-emerald">{it.pts}</span>
              </div>
              <h3 className="font-display text-xl font-bold mt-6">{it.t}</h3>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{it.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StudentScore() {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="eyebrow">Your EthicCred Score as a student</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            A score that opens doors before your first job.
          </h2>
          <p className="text-ec-ink-soft mt-5 leading-relaxed">
            From the moment you join, you start earning a verifiable Credibility Score. Reach
            Silver in your second year. Reach Gold by graduation. Walk into your first interview
            already trusted.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              "Tracked in real time — never editable",
              "Compared against millions of professionals globally",
              "Visible on your shareable Passport URL",
              "Travels with you — across colleges, jobs, continents",
            ].map((f) => (
              <li key={f} className="flex gap-3 items-start">
                <CheckCircle2 className="w-5 h-5 text-ec-emerald mt-0.5 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-6">
          {/* Mock student passport */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative bg-ec-soft border border-ec-ink/15 p-8 noise-overlay shadow-[0_30px_80px_-20px_rgba(0,0,0,0.18)]"
          >
            <div className="absolute inset-0 pointer-events-none opacity-[0.04] text-[100px] font-display font-black flex items-center justify-center select-none">
              ETHICCRED
            </div>
            <div className="relative">
              <div className="flex items-center justify-between">
                <div>
                  <div className="eyebrow">Student Passport</div>
                  <h3 className="font-display text-3xl font-black mt-2">Riya Sharma</h3>
                  <p className="text-ec-ink-soft text-sm">B.Tech CS · NIT Trichy · Class of 2027</p>
                </div>
                <div className="text-right">
                  <div className="eyebrow">EthicCred ID</div>
                  <div className="font-mono-plex font-bold text-base mt-1">EC-7G2H-S4MR</div>
                  <div className="mt-3 flex items-center gap-2 justify-end">
                    <span className="font-display font-black text-3xl tracking-tighter">540</span>
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-ec-amber border border-ec-amber/30 bg-ec-amber/10 px-2 py-0.5 rounded-full">
                      Gold
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-8 border-t border-dashed border-ec-ink/15 pt-6 grid grid-cols-2 gap-6">
                <Row label="Internships" v="2 · verified" />
                <Row label="Recommendations" v="3 · from professors" />
                <Row label="Projects" v="5 · capstone + hackathons" />
                <Row label="Conduct" v="A+ · institution-signed" />
              </div>
              <div className="absolute right-0 bottom-[-10px] stamp text-ec-emerald">Verified</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, v }) {
  return (
    <div>
      <div className="eyebrow">{label}</div>
      <div className="font-display font-semibold mt-1 text-base">{v}</div>
    </div>
  );
}

function Steps() {
  const steps = [
    { n: "01", t: "Sign up free", d: "Pick the Student role and get a global EthicCred ID in 60 seconds." },
    { n: "02", t: "Add your story", d: "List your degree, projects, clubs and skills. Anything self-declared shows as such until verified." },
    { n: "03", t: "Get records verified", d: "Your institution, professors, internship managers can sign records directly to your passport." },
    { n: "04", t: "Share with recruiters", d: "Give recruiters a QR or link. They see the verified version — no calls, no waiting." },
  ];
  return (
    <section className="bg-ec-soft border-y border-ec-line">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-5">
            <div className="eyebrow">How students get started</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
              Four steps. Forever yours.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-ec-line bg-white">
          {steps.map((s, i) => (
            <div
              key={s.n}
              className={`p-8 ${i < 3 ? "md:border-r md:border-ec-line" : ""} ${i < steps.length - 1 ? "border-b md:border-b-0 border-ec-line" : ""} hover:bg-ec-soft transition-colors`}
              data-testid={`student-step-${s.n}`}
            >
              <span className="font-mono-plex text-xs text-ec-ink-soft">{s.n}</span>
              <h3 className="font-display text-xl font-bold mt-6">{s.t}</h3>
              <p className="text-ec-ink-soft text-sm mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CampusPlacement() {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-24">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="eyebrow flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> For your placement cell</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
            Tell your TPO about EthicCred.
          </h2>
          <p className="text-ec-ink-soft mt-5 leading-relaxed">
            Institutions get a dedicated workspace to issue degrees, internship certificates,
            placement records and conduct certificates in bulk. Students walk in already verified;
            recruiters skip background checks.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="students-tpo-register">
              <Button className="rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2">Get my institution on board <ArrowUpRight className="w-4 h-4" /></Button>
            </Link>
            <a href="mailto:hello@ethiccred.com?subject=Campus%20Placement%20%E2%80%94%20EthicCred" data-testid="students-tpo-email">
              <Button variant="outline" className="rounded-sm border-ec-ink">Email a brochure</Button>
            </a>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 gap-4">
          {[
            { i: GraduationCap, t: "Bulk degree issuance" },
            { i: Briefcase, t: "Placement records" },
            { i: Award, t: "Conduct certificates" },
            { i: Star, t: "Faculty recommendations" },
          ].map((c) => (
            <div key={c.t} className="bg-white border border-ec-line p-6 hover:-translate-y-1 transition-transform">
              <c.i className="w-6 h-6 text-ec-emerald" />
              <div className="font-display font-semibold mt-4">{c.t}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function StudentPricing() {
  return (
    <section className="max-w-5xl mx-auto px-6 sm:px-10">
      <Card className="rounded-sm border-ec-ink overflow-hidden" data-testid="student-pricing">
        <CardContent className="p-0">
          <div className="grid md:grid-cols-5 items-stretch">
            <div className="md:col-span-3 p-10">
              <div className="eyebrow">Student plan</div>
              <h2 className="font-display text-4xl font-black tracking-tight mt-2">Always free for students.</h2>
              <p className="text-ec-ink-soft mt-3">
                Every EthicCred feature for individuals is free, forever. No card. No trial. No
                upgrade pressure. Build your passport while you're studying — keep it for life.
              </p>
              <ul className="mt-6 space-y-2 text-sm">
                {[
                  "Global EthicCred ID",
                  "Lifetime Professional Passport",
                  "0–1000 Credibility Score",
                  "Unlimited verified records",
                  "QR-based sharing & analytics",
                ].map((f) => (
                  <li key={f} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-ec-emerald mt-0.5" /> {f}</li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-2 bg-ec-ink text-white p-10 flex flex-col justify-between">
              <div>
                <div className="eyebrow text-zinc-400">Price</div>
                <div className="font-display text-7xl font-black tracking-tighter mt-2">$0</div>
                <div className="text-zinc-400 text-sm mt-1">forever</div>
              </div>
              <Link to="/register" data-testid="students-pricing-cta">
                <Button className="rounded-sm bg-white text-ec-ink hover:bg-zinc-200 w-full h-12 gap-2 mt-8">
                  Claim my Passport <ArrowUpRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16">
      <div className="relative bg-ec-ink text-white p-12 sm:p-16 overflow-hidden">
        <div className="absolute -right-20 -bottom-20 w-80 h-80 border border-white/10 rounded-full" />
        <div className="absolute -right-40 -bottom-40 w-[28rem] h-[28rem] border border-white/10 rounded-full" />
        <div className="relative grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="eyebrow text-zinc-400">Your career, signed at every step</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
              Start before you graduate.
            </h2>
            <p className="text-zinc-400 mt-4 max-w-xl">
              The earlier you start, the bigger your verified record by the time you sit in your
              first interview. Claim your Student Passport today — free, forever.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/register" data-testid="students-final-cta">
              <Button className="rounded-sm bg-white text-ec-ink hover:bg-zinc-200 h-12 px-6 gap-2">
                Claim my Passport <ArrowUpRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
